<div class="h-full flex flex-col bg-gray-100 rounded-2xl p-4">
  @let location = locationResource.value();
  @if (isNew || location) {
    <!-- Back button -->
    <div class="px-2 py-2">
      <button
        class="p-2 rounded-md hover:bg-gray-200 transition-colors"
        (click)="goBack()"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>
    </div>

    <div class="flex-1 overflow-auto px-2">
      <!-- Map on top -->
      @if (mapUrl(); as url) {
        <iframe
          [src]="url"
          class="w-full h-64 rounded-xl border-0"
          loading="lazy"
          referrerpolicy="no-referrer-when-downgrade"
          allowfullscreen
        ></iframe>
      }

      @if (!isEditing() && location) {
        <!-- View: name as title + Edit, then the details -->
        <div class="flex items-center justify-between mt-4 mb-4">
          <div class="flex items-center gap-3 min-w-0">
            <app-avatar
              size="lg"
              fallback="photo"
              [src]="location.avatarThumbnailUrl"
              [name]="location.name"
              [class.cursor-pointer]="!!location.avatarUrl"
              (click)="openLightbox()"
            />
            <h2 class="text-3xl font-bold truncate">{{ location.name }}</h2>
            @if (!isActive(location)) {
              <span class="shrink-0 rounded-full bg-gray-200 px-3 py-1 text-sm font-medium text-gray-700">Disabled</span>
            }
          </div>
          <div class="flex shrink-0 gap-2">
            @if (isActive(location)) {
              <button
                type="button"
                class="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 transition-colors"
                (click)="confirmingDisable.set(true)"
              >
                Disable
              </button>
            } @else {
              <button
                type="button"
                class="px-4 py-2 bg-green-600 text-white font-medium rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                [disabled]="isTogglingActive()"
                (click)="setActive(true)"
              >
                {{ isTogglingActive() ? 'Enabling…' : 'Enable' }}
              </button>
            }
            <button
              type="button"
              class="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
              (click)="startEdit()"
            >
              Edit
            </button>
          </div>
        </div>
        @if (errorMessage(); as message) {
          <div class="-mt-2 mb-4 text-sm text-red-600">{{ message }}</div>
        }

        <div class="space-y-4">
          <div>
            <span class="font-semibold">Street:</span>
            {{ location.streetName }}
          </div>
          <div>
            <span class="font-semibold">Postal code:</span>
            {{ location.postalCode || '—' }}
          </div>
          <div>
            <span class="font-semibold">City:</span>
            {{ location.city }}
          </div>
          <div>
            <span class="font-semibold">Phone:</span>
            @if (location.phone) {
              <a [href]="'tel:' + location.phone" class="text-blue-600 hover:underline">{{ location.phone }}</a>
            } @else {
              —
            }
          </div>
          <div>
            <span class="font-semibold">Email:</span>
            @if (location.email) {
              <a [href]="'mailto:' + location.email" class="text-blue-600 hover:underline">{{ location.email }}</a>
            } @else {
              —
            }
          </div>
          <div>
            <span class="font-semibold">Website:</span>
            @if (location.website) {
              <a
                [href]="location.website.startsWith('http') ? location.website : 'https://' + location.website"
                target="_blank"
                rel="noopener"
                class="text-blue-600 hover:underline"
              >{{ location.website }}</a>
            } @else {
              —
            }
          </div>
          <div>
            <span class="font-semibold">Description:</span>
            {{ location.description || '—' }}
          </div>
          <div>
            <span class="font-semibold">Working hours:</span>
            <div class="mt-1 space-y-0.5">
              @for (day of weekdays; track day.key) {
                @let intervals = location.workingHours?.[day.key];
                <div class="flex gap-2">
                  <span class="w-28 shrink-0 text-gray-600">{{ day.label }}</span>
                  @if (intervals?.length) {
                    <span>{{ formatDayHours(intervals) }}</span>
                  } @else {
                    <span class="text-gray-400">Closed</span>
                  }
                </div>
              }
            </div>
          </div>
        </div>
      } @else {
        <!-- Edit / create: name input as title with Cancel/Save, then the other fields -->
        <form [formGroup]="form" class="mt-4 space-y-4">
          <div>
            <div class="flex items-center gap-3">
              <!-- Avatar with upload button below, inline with the title (uploaded on Save) -->
              <app-avatar-picker
                fallback="photo"
                [currentUrl]="location?.avatarThumbnailUrl"
                [disabled]="isSaving()"
                [(file)]="avatarFile"
              />

              <input
                id="name"
                type="text"
                formControlName="name"
                (keydown.escape)="revertField('name', $event)"
                class="flex-1 min-w-0 text-3xl font-bold rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                placeholder="Location name"
              />
              <div class="flex gap-2">
                <button
                  type="button"
                  (click)="cancelEdit()"
                  [disabled]="isSaving()"
                  class="px-4 py-2 bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 font-medium rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  (click)="save()"
                  [disabled]="form.invalid || isSaving() || isLocating()"
                  class="px-4 py-2 bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                  {{ isSaving() ? 'Saving…' : isNew ? 'Create' : 'Save' }}
                </button>
              </div>
            </div>
            @if (avatarFile()) {
              <div class="text-sm text-gray-500 mt-1">New avatar will be saved when you click Save.</div>
            }
            @if (f.name.touched && f.name.invalid) {
              <div class="text-sm text-red-600 mt-1">Name is required.</div>
            }
            @if (errorMessage(); as message) {
              <div class="text-sm text-red-600 mt-1">{{ message }}</div>
            }
          </div>

          <div>
            <div class="flex items-center justify-between mb-1">
              <label for="streetName" class="font-medium">Street</label>
              <button
                type="button"
                (click)="showOnMap()"
                [disabled]="isLocating() || isSaving()"
                title="Show on map"
                aria-label="Show address on map"
                class="flex items-center gap-1 px-2 py-1 text-sm text-gray-700 font-medium rounded-md hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                @if (isLocating()) {
                  <app-icon name="spinner" class="w-4 h-4" />
                  Locating…
                } @else {
                  <app-icon name="refresh" class="w-4 h-4" />
                  Show on map
                }
              </button>
            </div>
            <input
              id="streetName"
              type="text"
              formControlName="streetName"
              (keydown.escape)="revertField('streetName', $event)"
              class="w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
              placeholder="Street name"
            />
            @if (f.streetName.touched && f.streetName.invalid) {
              <div class="text-sm text-red-600 mt-1">Street is required.</div>
            }
          </div>

          <div>
            <label for="postalCode" class="block mb-1 font-medium">Postal code</label>
            <input
              id="postalCode"
              type="text"
              formControlName="postalCode"
              (keydown.escape)="revertField('postalCode', $event)"
              class="w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
              placeholder="Postal code"
            />
          </div>

          <div>
            <label for="city" class="block mb-1 font-medium">City</label>
            <input
              id="city"
              type="text"
              formControlName="city"
              (keydown.escape)="revertField('city', $event)"
              class="w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
              placeholder="City"
            />
            @if (f.city.touched && f.city.invalid) {
              <div class="text-sm text-red-600 mt-1">City is required.</div>
            }
          </div>

          @if (addressWarning(); as warning) {
            <div role="status" class="flex gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <app-icon name="location" class="w-4 h-4 shrink-0 mt-0.5" />
              <span>{{ warning }}</span>
            </div>
          }

          <div>
            <label for="phone" class="block mb-1 font-medium">Phone</label>
            <input
              id="phone"
              type="tel"
              formControlName="phone"
              (keydown.escape)="revertField('phone', $event)"
              class="w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
              placeholder="Phone number"
            />
          </div>

          <div>
            <label for="email" class="block mb-1 font-medium">Email</label>
            <input
              id="email"
              type="email"
              formControlName="email"
              (keydown.escape)="revertField('email', $event)"
              class="w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
              placeholder="Email address"
            />
            @if (f.email.touched && f.email.invalid) {
              <div class="text-sm text-red-600 mt-1">Enter a valid email.</div>
            }
          </div>

          <div>
            <label for="website" class="block mb-1 font-medium">Website</label>
            <input
              id="website"
              type="url"
              formControlName="website"
              (keydown.escape)="revertField('website', $event)"
              class="w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
              placeholder="https://example.com"
            />
          </div>

          <div>
            <label for="description" class="block mb-1 font-medium">Description</label>
            <textarea
              id="description"
              rows="3"
              formControlName="description"
              (keydown.escape)="revertField('description', $event)"
              class="w-full rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 resize-none"
              placeholder="Short description of this location"
            ></textarea>
          </div>

          <div formGroupName="workingHours">
            <label class="block mb-2 font-medium">Working hours</label>
            <div class="space-y-3">
              @for (day of weekdays; track day.key) {
                <div [formArrayName]="day.key">
                  <div class="flex items-center justify-between mb-1">
                    <span class="font-medium text-gray-700">{{ day.label }}</span>
                    <button
                      type="button"
                      (click)="addInterval(day.key)"
                      class="px-2 py-1 text-sm bg-gray-200 text-gray-800 font-medium rounded-md hover:bg-gray-300 transition-colors"
                    >
                      Add interval
                    </button>
                  </div>
                  @if (intervalsFor(day.key).length) {
                    @for (
                      interval of intervalsFor(day.key).controls;
                      track $index
                    ) {
                      <div [formGroupName]="$index" class="flex items-center gap-2 mb-1">
                        <input
                          type="time"
                          formControlName="open"
                          class="rounded-md border border-gray-300 px-3 py-2 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                        />
                        <span class="text-gray-500">–</span>
                        <input
                          type="time"
                          formControlName="close"
                          class="rounded-md border border-gray-300 px-3 py-2 transition-colors focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30"
                        />
                        <button
                          type="button"
                          (click)="removeInterval(day.key, $index)"
                          class="px-2 py-1 text-sm text-red-600 font-medium rounded-md hover:bg-red-50 transition-colors"
                          aria-label="Remove interval"
                        >
                          Remove
                        </button>
                      </div>
                    }
                  } @else {
                    <div class="text-sm text-gray-400">Closed</div>
                  }
                </div>
              }
            </div>
          </div>
        </form>
      }
    </div>

    @if (confirmingDisable() && location) {
      <app-confirm-dialog
        confirmLabel="Disable"
        busyLabel="Disabling…"
        [busy]="isTogglingActive()"
        [error]="errorMessage()"
        (confirmed)="setActive(false)"
        (cancelled)="confirmingDisable.set(false)"
      >
        <ng-container dialogTitle>Disable {{ location.name }}?</ng-container>
        Customers <span class="font-semibold text-red-600">won't be able to send orders</span> to this location.
        Existing orders are not affected, and you can enable it again at any time.
      </app-confirm-dialog>
    }

    <!-- Full-size avatar lightbox -->
    @if (showLightbox() && location && location.avatarUrl) {
      <div
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
        (click)="closeLightbox()"
      >
        <button
          type="button"
          class="absolute top-4 right-4 p-2 rounded-md text-white hover:bg-white/20 transition-colors"
          (click)="closeLightbox()"
          aria-label="Close"
        >
          <img src="/close.svg" alt="" class="w-7 h-7 object-contain brightness-0 invert" />
        </button>
        <img
          [src]="location.avatarUrl"
          alt="{{ location.name }} avatar"
          class="max-w-full max-h-full rounded-lg object-contain"
          (click)="$event.stopPropagation()"
        />
      </div>
    }
  } @else if (locationResource.isLoading()) {
    <div class="flex items-center justify-center h-full">
      <div class="text-center text-gray-500">
        <div class="text-xl mb-2">Loading location details…</div>
      </div>
    </div>
  } @else {
    <div class="flex items-center justify-center h-full">
      <div class="text-center text-gray-500">
        <div class="text-xl mb-2">Location not found.</div>
      </div>
    </div>
  }
</div>
